import type { ApiQueryParams } from "@/types/api-types";

import type { LanguageSubset } from "./queries/general/queryOptions";

// Only add global query keys if you're certain they can be reused across projects
export const GLOBAL_QUERY_KEYS = {
  ALERTS: ["ALERTS"],
  ALERTS_LIST: () => [...GLOBAL_QUERY_KEYS.ALERTS, "LIST"] as const,

  ALERTS_TRIGGERED_LIST: (query: ApiQueryParams<"getTriggeredAlertsV1">, projectId: string | null) =>
    ["ALERTS_TRIGGERED", query, projectId] as const,

  AUTOMATED_SURVEYS: ["AUTOMATED_SURVEYS"],
  AUTOMATED_SURVEY_DETAILS: (id: string) => [...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "DETAILS", id] as const,
  AUTOMATED_SURVEY_LIST: (query?: ApiQueryParams<"getAutomatedSurveysV1">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "LIST", query] as const)
      : ([...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "LIST"] as const),
  AUTOMATED_SURVEY_ACTIVE_LIST: (id: string, query?: ApiQueryParams<"getAutomatedSurveysV1">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "ACTIVE_LIST", id, query] as const)
      : ([...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "ACTIVE_LIST", id] as const),

  CONFIGURATIONS: ["CONFIGURATIONS"],
  CONFIGURATIONS_APP_BRANDING: ["CONFIGURATION_APP_BRAND"],
  CONFIGURATIONS_APP_BRANDING_LIST: () =>
    [...GLOBAL_QUERY_KEYS.CONFIGURATIONS, ...GLOBAL_QUERY_KEYS.CONFIGURATIONS_APP_BRANDING, "LIST"] as const,

  SELF: (projectId?: string) => (projectId ? (["SELF", projectId] as const) : (["SELF"] as const)),
  SELF_PROJECTS: () => [...GLOBAL_QUERY_KEYS.SELF(), "PROJECTS"] as const,
  SELF_ACCOUNT: () => [...GLOBAL_QUERY_KEYS.SELF(), "ACCOUNT"],
  SELF_ACCOUNT_NOTIFICATION_SETTINGS: () => [...GLOBAL_QUERY_KEYS.SELF_ACCOUNT(), "NOTIFICATION_SETTINGS"] as const,

  INTERCOM_IDENTITY: ["INTERCOM_IDENTITY"],

  AVAILABLE_INTEGRATIONS: (language: string) => ["AVAILABLE_INTEGRATIONS", language] as const,
  AVAILABLE_INTEGRATIONS_DETAIL: (id: string, language: string) =>
    [...GLOBAL_QUERY_KEYS.AVAILABLE_INTEGRATIONS(language), id] as const,

  LANGUAGES_BASE: ["LANGUAGES_BASE"],
  LANGUAGES: (options?: LanguageSubset) => (options ? (["LANGUAGES", options] as const) : (["LANGUAGES"] as const)),

  MESSAGE_TEMPLATES: (query?: ApiQueryParams<"getMessageTemplatesV1">) => ["MESSAGE_TEMPLATES", query] as const,

  MASS_MESSAGES: ["MASS_MESSAGES"],
  MASS_MESSAGES_PROJECTS: (query: ApiQueryParams<"getMassMessagesProjectsV1">) =>
    [...GLOBAL_QUERY_KEYS.MASS_MESSAGES, "PROJECTS", query] as const,
  MASS_MESSAGES_GROUPS: (query: ApiQueryParams<"getMassMessagesGroupsV1">) =>
    [...GLOBAL_QUERY_KEYS.MASS_MESSAGES, "GROUPS", query] as const,

  MASS_SURVEYS: ["MASS_SURVEYS"],
  MASS_SURVEY_GROUPS: () => [...GLOBAL_QUERY_KEYS.MASS_SURVEYS, "GROUPS"] as const,
  MASS_SURVEY_PROJECTS: (groupId?: string) =>
    groupId
      ? ([...GLOBAL_QUERY_KEYS.MASS_SURVEYS, "PROJECTS", groupId] as const)
      : ([...GLOBAL_QUERY_KEYS.MASS_SURVEYS, "PROJECTS"] as const),

  PLATFORM_GROUPS: ["PLATFORM_GROUPS"],
  PLATFORM_GROUPS_LIST: (query?: ApiQueryParams<"getSystemGroupsV1">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.PLATFORM_GROUPS, "LIST", query] as const)
      : ([...GLOBAL_QUERY_KEYS.PLATFORM_GROUPS, "LIST"] as const),
  PLATFORM_GROUP_DETAILS: (id: string) => [...GLOBAL_QUERY_KEYS.PLATFORM_GROUPS, "DETAILS", id] as const,

  PORTFOLIO: ["PORTFOLIO"],
  PORTFOLIO_LIST: (month?: string) =>
    month
      ? ([...GLOBAL_QUERY_KEYS.PORTFOLIO, "LIST", month] as const)
      : ([...GLOBAL_QUERY_KEYS.PORTFOLIO, "LIST"] as const),
  PORTFOLIO_BENCHMARK: (month: string) => [...GLOBAL_QUERY_KEYS.PORTFOLIO, "BENCHMARK", month] as const,
  // TODO: Enhance type safety by restricting 'type' to specific string literals
  PORTFOLIO_ANALYTICS: (type: string, query?: object) =>
    query
      ? [...GLOBAL_QUERY_KEYS.PORTFOLIO, "ANALYTICS", type, query]
      : ([...GLOBAL_QUERY_KEYS.PORTFOLIO, "ANALYTICS", type] as const),

  PORTFOLIO_PERMISSIONS: ["PORTFOLIO_PERMISSIONS"] as const,

  PORTFOLIO_INTEGRATIONS: ["PORTFOLIO_INTEGRATIONS"] as const,
  PORTFOLIO_INTEGRATION_CONFIGURATIONS: (kind: string) =>
    [...GLOBAL_QUERY_KEYS.PORTFOLIO_INTEGRATIONS, "CONFIGURATIONS", kind] as const,

  PORTFOLIO_CHATS: ["PORTFOLIO_CHATS"],
  PORTFOLIO_CHATS_LIST: (query?: ApiQueryParams<"getPortfolioChatsV2">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.PORTFOLIO_CHATS, "LIST", query] as const)
      : ([...GLOBAL_QUERY_KEYS.PORTFOLIO_CHATS, "LIST"] as const),
  PORTFOLIO_CHATS_DETAILS: (id: string) => [...GLOBAL_QUERY_KEYS.PORTFOLIO_CHATS, "DETAILS", id] as const,
  PORTFOLIO_USER_CHAT_DETAILS: (userId: string) =>
    [...GLOBAL_QUERY_KEYS.PORTFOLIO_CHATS, "USER_DETAILS", userId] as const,
  PORTFOLIO_CHATS_REPLIES: (id: string) => [...GLOBAL_QUERY_KEYS.PORTFOLIO_CHATS, "REPLIES", id] as const,
  PORTFOLIO_CHATS_AVAILABLE_USERS: (query?: ApiQueryParams<"getPortfolioChatsAvailableUsersV2">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.PORTFOLIO_CHATS, "AVAILABLE_USERS", query] as const)
      : ([...GLOBAL_QUERY_KEYS.PORTFOLIO_CHATS, "AVAILABLE_USERS"] as const),

  PORTFOLIO_TICKETS: ["PORTFOLIO_TICKETS"],
  PORTFOLIO_TICKETS_LIST: (query?: ApiQueryParams<"getAdminPortfolioTicketsV1">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.PORTFOLIO_TICKETS, "LIST", query] as const)
      : ([...GLOBAL_QUERY_KEYS.PORTFOLIO_TICKETS, "LIST"] as const),
  PORTFOLIO_TICKETS_IDS: (query?: ApiQueryParams<"getAdminPortfolioTicketsV1">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.PORTFOLIO_TICKETS, "IDS", query] as const)
      : ([...GLOBAL_QUERY_KEYS.PORTFOLIO_TICKETS, "IDS"] as const),
  PORTFOLIO_TICKETS_FILTERS: (query: ApiQueryParams<"getAdminPortfolioTicketsFiltersV1">) =>
    [...GLOBAL_QUERY_KEYS.PORTFOLIO_TICKETS, "FILTERS", query] as const,
  PORTFOLIO_TICKETS_TABS_AMOUNT: (query: ApiQueryParams<"getAdminPortfolioTicketsStatsAllTabsV1">) =>
    [...GLOBAL_QUERY_KEYS.PORTFOLIO_TICKETS, "TAB_AMOUNT", query] as const,
  PORTFOLIO_TICKETS_TAB_TOTAL: (query: ApiQueryParams<"getAdminPortfolioTicketsStatsDetailsV1">) =>
    [...GLOBAL_QUERY_KEYS.PORTFOLIO_TICKETS, "TAB_TOTAL", query] as const,

  PROJECT_CONNECTIONS: ["PROJECT_CONNECTIONS"],
  PROJECT_CONNECTION_LIST_INCLUDE_ALL: (projectId: string, query: ApiQueryParams<"getProjectConnectionsV1">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.PROJECT_CONNECTIONS, "LIST", projectId, query] as const)
      : ([...GLOBAL_QUERY_KEYS.PROJECT_CONNECTIONS, "LIST", projectId] as const),
  PROJECT_CONNECTION_LIST: (query?: ApiQueryParams<"getProjectConnectionsV1">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.PROJECT_CONNECTIONS, "LIST", query] as const)
      : ([...GLOBAL_QUERY_KEYS.PROJECT_CONNECTIONS, "LIST"] as const),
  PROJECT_CONNECTION_DETAILS: (id: string) => [...GLOBAL_QUERY_KEYS.PROJECT_CONNECTIONS, "DETAILS", id] as const,

  PROJECT_CONNECTION_GROUPS: ["PROJECT_CONNECTION_GROUPS"],
  PROJECT_CONNECTION_GROUPS_LIST: (id: string, query?: { Offset: number; Limit: number }) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.PROJECT_CONNECTION_GROUPS, "LIST", id, query] as const)
      : ([...GLOBAL_QUERY_KEYS.PROJECT_CONNECTION_GROUPS, "LIST", id] as const),
  PROJECT_CONNECTION_GROUP_DETAILS: (id: string) =>
    [...GLOBAL_QUERY_KEYS.PROJECT_CONNECTION_GROUPS, "DETAILS", id] as const,

  TICKET_SETTINGS: ["TICKET_SETTINGS"] as const,

  TRANSLATIONS: (language: string) => ["TRANSLATIONS", language] as const,

  PROJECT_INVITE_LIST: ["PROJECT_INVITE_LIST"] as const,

  BULK_UPLOAD_ADDRESSES_PROJECTS: () => ["BULK_UPLOAD_ADDRESSES_PROJECTS"] as const,
  BULK_UPLOAD_ADDRESSES_BUILDINGS: () => ["BULK_UPLOAD_ADDRESSES_BUILDING"] as const,

  ASSISTANT_CONVERSATIONS: ["ASSISTANT_CONVERSATIONS"],
  ASSISTANT_CONVERSATIONS_LIST: () => [...GLOBAL_QUERY_KEYS.ASSISTANT_CONVERSATIONS, "LIST"] as const,
  ASSISTANT_CONVERSATION: (conversationId: string) =>
    [...GLOBAL_QUERY_KEYS.ASSISTANT_CONVERSATIONS, conversationId] as const,
  ASSISTANT_CONVERSATION_MESSAGES: (conversationId: string) =>
    [...GLOBAL_QUERY_KEYS.ASSISTANT_CONVERSATION(conversationId), "MESSAGES"] as const,

  NOTIFICATION_SETTING: ["NOTIFICATION_SETTINGS"],
  NOTIFICATION_SETTINGS_DEFAULT: () => [...GLOBAL_QUERY_KEYS.NOTIFICATION_SETTING, "DEFAULT"] as const,

  USERS_LOOKUP: (query: ApiQueryParams<"getUsersLookupV1">) => ["USERS_LOOKUP", query] as const,
} as const;
